import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import cx from 'classnames';
import { sendUserToRoleHomePage } from '../../GlobalFunctions';
import { LoginState } from '../../UserManagement';
import { FreeTrial, RequestInfoButton } from './HeaderButtons';
import MobilePanelMenu from './MobilePanelMenu';
import HeaderNavItem from './HeaderNavItem';
import { navItems } from './navItems';
import logoBookmark from '../../images/LOGO.svg';
import logoBookmarkMobile from '../../images/pkp-peekapak-logo-mobile.svg';
import styles from './UnauthenticatedHeader.module.scss';
import { useMediaQuery } from 'react-responsive';
import { computeBookingUrl } from '../CTASegment';
import useCalendly from '../../openCalendly';
import { ExternalLink } from '../ExternalLink';

interface UserProfile {
  role?: string;
  // Add other user profile properties as needed
}

interface RootState {
  user: {
    userProfile: UserProfile;
    loginState: LoginState;
  };
}

interface SubMenuItem {
  name: string;
  route: string;
  icon?: string;
  description?: string;
}

interface HeaderProps {
  userProfile: UserProfile;
  loginState: LoginState;
}

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
  loginState: state.user.loginState,
});

const Header = (props: HeaderProps) => {
  const [isMobileMenuActive, setIsMobileMenuActive] = useState(false);
  const [k12Offset, setK12Offset] = useState(0);
  const [menuWidth, setMenuWidth] = useState(0);
  const [activeSubItems, setActiveSubItems] = useState<SubMenuItem[]>([]);

  const k12Ref = useRef<HTMLLIElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const history = useHistory();
  const isMobile = useMediaQuery({ maxWidth: 1279 });
  const logoToUse = isMobile ? logoBookmarkMobile : logoBookmark;

  const bookingUrl = computeBookingUrl();
  const openCalendly = useCalendly(bookingUrl);

  useEffect(() => {
    const updateOffsets = () => {
      if (!isMobile && k12Ref.current && containerRef.current) {
        const k12Bounds = k12Ref.current.getBoundingClientRect();
        const containerBounds = containerRef.current.getBoundingClientRect();

        const leftOffset = k12Bounds.left - containerBounds.left;
        const dropdownWidth = containerBounds.width - leftOffset;

        setK12Offset(leftOffset);
        setMenuWidth(dropdownWidth);
      }
    };

    const timeoutId = setTimeout(updateOffsets, 0);
    window.addEventListener('resize', updateOffsets);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateOffsets);
    };
  }, [isMobile]);

  // Clear active submenu when switching to mobile
  useEffect(() => {
    if (isMobile) {
      setActiveSubItems([]);
      // Clear any pending timeouts when switching to mobile
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }
  }, [isMobile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const navigateHome = () => {
    if (props.loginState === LoginState.loggedIn) {
      sendUserToRoleHomePage(props.userProfile, history, false, true);
    } else {
      history.push('/');
    }
  };

  const handleNavItemHover = (subItems: SubMenuItem[]) => {
    if (!isMobile) {
      // Clear any existing timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setActiveSubItems(subItems || []);
    }
  };

  const handleNavItemLeave = () => {
    if (!isMobile) {
      // Add a small delay before closing to allow cursor to reach the dropdown
      closeTimeoutRef.current = setTimeout(() => {
        setActiveSubItems([]);
      }, 100);
    }
  };

  const handleDropdownEnter = () => {
    // Clear the timeout when entering the dropdown
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleDropdownLeave = () => {
    setActiveSubItems([]);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuActive(!isMobileMenuActive);
  };

  const handleSetMobileMenuInactive = () => {
    // This is intentionally empty as it's used in desktop mode where mobile menu is not applicable
  };

  const containerRef = useRef(null);

  return (
    <header className={styles.myHeader}>
      <MobilePanelMenu active={isMobileMenuActive} setIsMobileMenuActive={setIsMobileMenuActive} history={history} />

      <div className={styles.logoWrapper}>
        <Link to='/' onClick={() => navigateHome()}>
          <img alt='Backpack' src={logoToUse} className={styles.logo} />
        </Link>
      </div>

      <MediaQuery maxWidth={1279}>
        {/* Mobile Header */}
        <div className={styles.mobileHeader}>
          <div className={cx(styles.hamburgerMenu, { [`${styles.active}`]: isMobileMenuActive })}>
            <button className={styles.resembleLinkButton} onClick={handleMobileMenuToggle}>
              <i className={cx('fa', 'fa-bars')} />
            </button>
          </div>
        </div>
      </MediaQuery>

      <MediaQuery minWidth={1280}>
        <div className={styles.topRow}>
          <div className={styles.topRowRight}>
            <div className={styles.topRowRightLinks}>
              <a href='https://help.peekapak.com' className={styles.phoneNumber}>
                Help Center
              </a>
              <a href='tel:+18885136418' className={styles.phoneNumber}>
                1-888-513-6418
              </a>
              <a href='/login' className={styles.phoneNumber}>
                Login
              </a>
            </div>
            <div className={styles.trialButton}>
              <FreeTrial isMobileMenuActive={false} setIsMobileMenuActive={handleSetMobileMenuInactive} />
            </div>
            <div className={styles.demoButton}>
              <RequestInfoButton isMobileMenuActive={false} setIsMobileMenuActive={handleSetMobileMenuInactive} />
            </div>
          </div>
        </div>

        <nav className={styles.bottomRow} ref={containerRef}>
          <ul className={styles.navItems}>
            {navItems
              .filter((item) => item.name !== 'LOGIN')
              .map((c, index) => (
                <HeaderNavItem
                  key={`HeaderNavItem-${index}`}
                  name={c.name}
                  description={c.description}
                  icon={c.icon}
                  route={c.route}
                  hasSubMenu={!!c.subItems}
                  history={history}
                  refEl={c.name === 'K12 SOLUTIONS' ? k12Ref : undefined}
                  onHover={() => handleNavItemHover(c.subItems || [])}
                  onLeave={handleNavItemLeave}
                />
              ))}
          </ul>

          {activeSubItems.length > 0 && !isMobile && (
            <div
              className={styles.dropdownBox}
              style={{
                left: `${k12Offset}px`,
                width: `${menuWidth}px`,
                position: 'absolute',
                top: '100%',
              }}
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <div className={styles.dropdownLeft}>
                {activeSubItems.map((sm, index) => {
                  if (sm.route.includes('http')) {
                    return (
                      <a className={styles.dropdownItem} href={sm.route} target='_blank' rel='noreferrer' key={index}>
                        <div className={styles.icon}>
                          <i className={sm.icon} />
                        </div>
                        <div className={styles.content}>
                          <div className={styles.title}>{sm.name}</div>
                          <div className={styles.description}>{sm.description}</div>
                        </div>
                      </a>
                    );
                  }

                  return (
                    <Link to={sm.route} key={index} className={styles.dropdownItem}>
                      <div className={styles.icon}>
                        <i className={sm.icon} />
                      </div>
                      <div className={styles.content}>
                        <div className={styles.title}>{sm.name}</div>
                        <div className={styles.description}>{sm.description}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className={styles.dropdownRight}>
                <div className={styles.staticItem} onClick={() => window.open('https://help.peekapak.com', '_blank')}>
                  <div className={styles.icon}>
                    <i className='fa fa-question-circle' />
                  </div>
                  <div className={styles.content}>
                    <div className={styles.title}>Help Center</div>
                    <div className={styles.description}>Help articles and videos</div>
                  </div>
                </div>
                <div className={styles.staticItem} onClick={() => openCalendly()}>
                  <div className={styles.icon}>
                    <i className='fa fa-calendar-alt' />
                  </div>
                  <div className={styles.content}>
                    <div className={styles.title}>Schedule a Demo</div>
                    <div className={styles.description}>See Peekapak in action</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
      </MediaQuery>
    </header>
  );
};

export default connect(mapStateToProps)(Header);
